<template>
  <div class="group">
    <div class="group__wrapper">
      <p class="group__order">{{ order }}</p>
      <h6 class="group__title">{{ group.name }}</h6>
      <div v-if="isAuthenticated && progress" class="group__progress-wrapper">
        <radial-progress-bar
          :diameter="30"
          :strokeWidth="3"
          :completed-steps="progress.completed"
          startColor="#00DC9B"
          :total-steps="progress.total"
        />
        <p class="group__completed">{{ progressPercentage }}%</p>
      </div>
    </div>
    <div class="group__content">
      <Content
        v-for="item in group.content"
        :key="item.uuid"
        :content="prepareContent(item)"
        :isAuthenticated="isAuthenticated"
        :plansLink="plansLink"
        :learningAreaName="learningAreaName"
      />
    </div>
  </div>
</template>

<script>
import RadialProgressBar from 'vue-radial-progress'
import Content from '@/components/learning-path/Content.vue'
import CONSTANTS from '@/constants'

export default {
  name: 'Group',
  components: {
    RadialProgressBar,
    Content,
  },
  props: {
    group: {
      type: Object,
      required: true,
    },
    order: {
      type: Number,
      required: true,
    },
    isAuthenticated: {
      type: Boolean,
      required: true,
    },
    plansLink: {
      type: String,
      required: true,
    },
    learningAreaName: {
      type: String,
      required: true,
    },
    videos: {
      type: Array,
      required: true,
    },
    quizzes: {
      type: Array,
      required: true,
    },
    exercises: {
      type: Array,
      required: true,
    },
    labs: {
      type: Array,
      required: true,
    },
    iframes: {
      type: Array,
      required: true,
    },
  },
  computed: {
    progress() {
      const progress = { completed: 0, total: 0 }
      if (this.isAuthenticated) {
        // @ToDo: support all types of content as leaves of a LP
        this.group.content.map(level => {
          switch (level.content_type) {
            case 'course': {
              progress.total += level.user_status.total_videos
              progress.total += level.user_status.total_quizzes
              progress.total += level.user_status.total_exercises
              progress.total += level.user_status.total_iframes
              progress.total += level.user_status.total_labs
              progress.completed += level.user_status.finished_videos
              progress.completed += level.user_status.finished_quizzes
              progress.completed += level.user_status.finished_exercises
              progress.completed += level.user_status.finished_iframes
              progress.completed += level.user_status.finished_labs
              break
            }
            default: {
              const { content_type: type, uuid } = level
              const current = this.getCurrentContent({ type, uuid })
              if (current) {
                progress.total += 1
                const { user_status } = current
                if (
                  user_status &&
                  user_status === CONSTANTS.CONTENT_USER_STATUS.FINISHED
                )
                  progress.completed += 1
              }
            }
          }
        })
      }
      return progress
    },
    progressPercentage() {
      if (this.isAuthenticated && this.progress.completed > 0) {
        return Math.round((100 * this.progress.completed) / this.progress.total)
      }
      return 0
    },
  },
  methods: {
    prepareContent(content) {
      const { content_type: ui_content_type, uuid } = content
      if (ui_content_type === 'course') {
        return { ...content, ui_content_type }
      }
      if (ui_content_type === 'video') {
        const file = this.videos.find(vid => vid.id === uuid)
        return { ...file, ui_content_type }
      }
      if (ui_content_type === 'quiz') {
        const file = this.quizzes.find(i => i.id === uuid)
        return { ...file, ui_content_type }
      }
      if (ui_content_type === 'exercise') {
        const file = this.exercises.find(i => i.id === uuid)
        return { ...file, ui_content_type }
      }
      if (ui_content_type === 'lab') {
        const file = this.labs.find(i => i.id === uuid)
        return { ...file, ui_content_type }
      }
      if (ui_content_type === 'iframe') {
        const file = this.iframes.find(i => i.id === uuid)
        return { ...file, ui_content_type }
      }
      return { ...content, ui_content_type }
    },
    getCurrentContent({ type, uuid }) {
      switch (type) {
        case 'video':
          return this.videos.find(video => video.id === uuid)
        case 'quiz':
          return this.quizzes.find(quiz => quiz.id === uuid)
        case 'exercise':
          return this.exercises.find(exercise => exercise.id === uuid)
        case 'lab':
          return this.labs.find(lab => lab.id === uuid)
        case 'iframe':
          return this.iframes.find(iframe => iframe.id === uuid)
        default:
          return { user_status: CONSTANTS.CONTENT_USER_STATUS.UNSTARTED }
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.group {
  &__wrapper {
    display: flex;
    align-items: center;
    width: 100%;
    background-color: var(--light-gray);
    height: 50px;
  }
  &__order {
    color: var(--white);
    font-size: 2.5rem;
    margin: 0;
    background-color: var(--black);
    height: 100%;
    display: flex;
    align-items: center;
    padding: 0;
    @include mobile {
      padding: 0 25px;
    }
  }
  &__title {
    color: var(--white);
    margin: 0 0 0 15px;
    font-size: 1.2rem;
    @include mobile {
      font-size: 1.3rem;

      margin: 0 30px;
    }
  }
  &__progress-wrapper {
    display: flex;
    align-items: center;
    margin: 0 5px 0 auto;
    @include mobile {
      margin: 0 50px 0 auto;
    }
  }
  &__completed {
    color: var(--white);
    margin: 0;
    margin-left: 5px;
  }
}
</style>
